import { HsCameraType } from '../../engine-new/enums/camera-type.enum';
import { HsClickControlAction } from '../actions/click-control-action.class';
import { HsControlActionType } from '../actions/control-action-type.enum';
import type { HsControlAction } from '../actions/control-action.abstract';
import { HsKeyboardControlAction } from '../actions/keyboard-control-action.class';
import { HsMouseMoveControlAction } from '../actions/mouse-move-control-action.class';
import { HsScrollControlAction } from '../actions/scroll-control-action.class';
import { HsZoomControlAction } from '../actions/zoom-control-action.class';
import { HsCameraTypeControlAction } from '../actions/camera-type-control-action.class';
import { HsCameraToolTypeControlAction } from '../actions/camera-tool-type-control-action.class';
import { HsCameraToolType } from '../actions/camera-tool-type.enum';
import { HsBehaviorSubject } from '../../reactive-new/classes/behavior.subject';
import { HsVector2 } from '../../math-new/objects/vector2.class';

export class HsControlActionsHandler {
  public readonly controlActionsMap: Map<HsControlActionType, HsControlAction> = new Map<
    HsControlActionType,
    HsControlAction
  >([
    [HsControlActionType.LeftClick, new HsClickControlAction(HsControlActionType.LeftClick)],

    [HsControlActionType.RightMove, new HsMouseMoveControlAction(HsControlActionType.RightMove)],
    [HsControlActionType.LeftMove, new HsMouseMoveControlAction(HsControlActionType.LeftMove)],
    [HsControlActionType.MiddleMove, new HsMouseMoveControlAction(HsControlActionType.MiddleMove)],

    [HsControlActionType.Scroll, new HsScrollControlAction()],
    [HsControlActionType.Zoom, new HsZoomControlAction()],

    [HsControlActionType.PressedRight, new HsKeyboardControlAction(HsControlActionType.PressedRight)],
    [HsControlActionType.PressedTop, new HsKeyboardControlAction(HsControlActionType.PressedTop)],
    [HsControlActionType.PressedLeft, new HsKeyboardControlAction(HsControlActionType.PressedLeft)],
    [HsControlActionType.PressedBottom, new HsKeyboardControlAction(HsControlActionType.PressedBottom)],

    [HsControlActionType.PressedPlus, new HsKeyboardControlAction(HsControlActionType.PressedPlus)],
    [HsControlActionType.PressedMinus, new HsKeyboardControlAction(HsControlActionType.PressedMinus)],

    [HsControlActionType.PressedCtrl, new HsKeyboardControlAction(HsControlActionType.PressedCtrl)],
    [HsControlActionType.PressedShift, new HsKeyboardControlAction(HsControlActionType.PressedShift)],
    [HsControlActionType.PressedEscape, new HsKeyboardControlAction(HsControlActionType.PressedEscape)],
    [HsControlActionType.PressedBackspace, new HsKeyboardControlAction(HsControlActionType.PressedBackspace)],

    [HsControlActionType.PressedAdditionalOne, new HsKeyboardControlAction(HsControlActionType.PressedAdditionalOne)],
    [HsControlActionType.PressedAdditionalTwo, new HsKeyboardControlAction(HsControlActionType.PressedAdditionalTwo)],

    [HsControlActionType.CameraToolType, new HsCameraToolTypeControlAction(HsCameraToolType.Default)],
    [HsControlActionType.CameraType, new HsCameraTypeControlAction(HsCameraType.Perspective)],
  ]);

  public readonly activeCameraToolType$: HsBehaviorSubject<HsCameraToolType> = new HsBehaviorSubject<HsCameraToolType>(
    HsCameraToolType.Default
  );

  public readonly activeCameraType$: HsBehaviorSubject<HsCameraType> = new HsBehaviorSubject<HsCameraType>(
    HsCameraType.Perspective
  );

  public readonly accumulatedLeftMouseDelta: HsVector2 = new HsVector2(0, 0);

  public getAction(actionType: HsControlActionType): HsControlAction | undefined {
    return this.controlActionsMap.get(actionType);
  }

  public updateControlAction(sourceAction: HsControlAction): void {
    const actionType: HsControlActionType = sourceAction.actionType;
    const updatedAction: HsControlAction | undefined = this.controlActionsMap.get(actionType);

    if (updatedAction === undefined) {
      return;
    }

    if (sourceAction instanceof HsCameraTypeControlAction) {
      this.activeCameraType$.next(sourceAction.cameraType);
    }

    if (sourceAction instanceof HsCameraToolTypeControlAction) {
      this.activeCameraToolType$.next(sourceAction.cameraToolType);
    }

    if (sourceAction instanceof HsMouseMoveControlAction && sourceAction.actionType === HsControlActionType.LeftMove) {
      this.increaseAccumulatedLeftMouseDelta(sourceAction.delta);
    }

    updatedAction.getValuesFrom(sourceAction);
  }

  public increaseAccumulatedLeftMouseDelta(actionMouseDelta: HsVector2): void {
    this.accumulatedLeftMouseDelta.add(actionMouseDelta);
  }

  public resetAccumulatedLeftMouseDeltaValues(): void {
    this.accumulatedLeftMouseDelta.multiplyByScalar(0);
  }

  public resetControlActions(): void {
    for (const controlAction of this.controlActionsMap.values()) {
      controlAction.setInactive();
    }
    this.resetAccumulatedLeftMouseDeltaValues();
  }
}
